import throttle from 'lodash/throttle';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import Sentry from '@lib/analytics/sentry';
import { type PayeeDetails } from '@lib/entities/ApplePay';
import { type Order } from '@lib/entities/Order';
import { isApplePayPayment } from '@lib/entities/Payment';
import { useApplePay } from '@lib/hooks/useApplePay';
import useToast from '@lib/hooks/useToast';
import useWalletRedux from '@lib/hooks/useWalletRedux';
import { selectCart } from '@lib/redux/slices/cartSlice';
import { selectCheckoutIsSubmitting, submitOrderRequest } from '@lib/redux/slices/checkoutSlice';
import { resetIsReorder } from '@lib/redux/slices/orderSlice';
import { type RootState } from '@lib/redux/store';

/** Leading-edge window: two taps inside it must not place two orders. */
const PLACE_ORDER_THROTTLE_MS = 2000;

/**
 * Carries a settle callback into the throttled submit. `started` is flipped synchronously
 * by the throttled body, so a still-false value means the call was DROPPED inside the
 * window — no saga runs, and nothing would otherwise settle the promise (JSAD-7104).
 */
interface SubmitTicket {
    started: boolean;
    onSettled: (placed: boolean) => void;
}

export interface UseFastReorderPlaceOrderParams {
    /** Order placed — dismiss the modal and go to the confirmation screen. */
    onSuccess: (order: Order) => void;
    /** Payment declined — dismiss, toast, and drop the user in the bag. */
    onFallbackToBag: () => void;
    /**
     * Shows the hCaptcha widget. Mirrors checkout's `handleCheckout(onShowCaptcha)`:
     * `placeOrder` DEFERS rather than submitting, and the caller resumes by invoking the
     * returned `placeOrderWithCaptcha(token)` once the widget verifies.
     *
     * Required for saved cards: the backend treats `hct` as mandatory on
     * `endpointVersion >= 2` (PaymentProcessCommand.cs:2467) and answers a missing token
     * with `"Unable to process CC"` — the same string it uses for a genuine card decline,
     * so Fast Reorder failed while telling the user their card was bad (2026-09-22).
     * Apple Pay skips it: the backend bypasses the check for those (`BypassForApplePay`).
     */
    onShowCaptcha: () => void;
}

/**
 * Places the Fast Reorder from the confirm modal (JSAD-6336).
 *
 * Deliberately NOT `useCheckoutScreen.shared.ts`: mounting that hook dispatches
 * `checkout/screenUnmounted` on teardown, whose saga clears quote/summary — closing this
 * modal would wipe the live quote out from under the page the user returns to. It also runs
 * its own quote effect and ~480 lines of tip/reward/promo/gift-card surface this screen does
 * not offer.
 *
 * What IS reused from it, because each is load-bearing: the throttle window (two taps in one
 * frame must not place two orders), the `paramsRef` so the throttled identity stays stable,
 * and `throttled.cancel()` on error so a retry does not wait the window out.
 */
export const useFastReorderPlaceOrder = (params: UseFastReorderPlaceOrderParams) => {
    const dispatch = useDispatch();
    const store = useStore<RootState>();
    const { toast } = useToast();
    const { selectedCard } = useWalletRedux();
    const { processApplePayPayment } = useApplePay();
    const cart = useSelector(selectCart);
    const isSubmitting = useSelector((state: RootState) => selectCheckoutIsSubmitting(state));
    const [isApplePayProcessing, setIsApplePayProcessing] = useState(false);
    const [isAwaitingCaptcha, setIsAwaitingCaptcha] = useState(false);

    // The actual reentrancy guard, same split web checkout uses: the ref flips on the line
    // the press runs, while the state above only lands on the next render — so a second tap
    // in the same frame would otherwise get through.
    const submittingRef = useRef(false);

    // Read through a ref so the throttled function's identity stays stable — otherwise its
    // internal timestamp resets every render and the throttle does nothing.
    const paramsRef = useRef(params);
    useEffect(() => {
        paramsRef.current = params;
    });

    const submit = useMemo(() => {
        const throttled = throttle(
            (
                captchaToken?: string,
                applePayToken?: string,
                payeeDetails?: PayeeDetails,
                ticket?: SubmitTicket
            ) => {
                if (ticket) {
                    ticket.started = true;
                }
                dispatch(
                    submitOrderRequest({
                        captchaToken,
                        applePayToken,
                        payeeDetails,
                        onSuccess: (order: Order) => {
                            paramsRef.current.onSuccess(order);
                        },
                        onError: (error: unknown) => {
                            Sentry.captureException(error);
                            throttled.cancel();

                            // The saga hands `onError` a bare Error, but it dispatches
                            // submitOrderFailure with the kind first — so the classification
                            // is already in Redux by the time this runs. Read it there
                            // rather than sniffing the message string.
                            if (store.getState().checkout.errorKind === 'payment') {
                                dispatch(resetIsReorder());
                                toast({
                                    level: 'warning',
                                    message:
                                        'Fast Reorder unavailable. Payment failed, please try ' +
                                        'another method at checkout.',
                                    timeout: 5000
                                });
                                paramsRef.current.onFallbackToBag();
                            }
                        },
                        onSettled: ticket?.onSettled
                    })
                );
            },
            PLACE_ORDER_THROTTLE_MS,
            { leading: true, trailing: false }
        );
        return throttled;
    }, [dispatch, store, toast]);

    /**
     * Awaitable submit. Apple Pay's callback contract is `Promise<boolean>` and the resolved
     * value decides whether the on-device sheet shows the checkmark or "Payment Not
     * Completed" (JSAD-7104) — so this must settle on every path, including a call dropped
     * inside the throttle window, where no saga runs at all.
     */
    const submitAsync = useCallback(
        (captchaToken?: string, applePayToken?: string, payeeDetails?: PayeeDetails) =>
            new Promise<boolean>((resolve) => {
                const ticket: SubmitTicket = { started: false, onSettled: resolve };
                submit(captchaToken, applePayToken, payeeDetails, ticket);
                if (!ticket.started) {
                    resolve(false);
                }
            }),
        [submit]
    );

    const placeOrder = useCallback(() => {
        // Ref first, mirroring web checkout's `submittingRef`: `isSubmitting` only flips
        // once the SAGA starts, which on the saved-card path is after the captcha has come
        // and gone. Without this the Confirm button stays live for the whole time the
        // widget is up, and a second tap would show a second challenge.
        if (submittingRef.current) {
            return;
        }
        submittingRef.current = true;
        setIsAwaitingCaptcha(true);

        // Apple Pay: no captcha. The backend bypasses the check for Apple Pay
        // (`BypassForApplePay`), and checkout likewise submits those with no token — so
        // demanding one here would add a puzzle nothing requires. The sheet is the only
        // extra step, exactly as at checkout.
        if (isApplePayPayment(selectedCard)) {
            // The sheet owns its own lifecycle from here, as it does at web checkout.
            submittingRef.current = false;
            setIsAwaitingCaptcha(false);
            const total = cart.summary?.total;
            if (!total) {
                return;
            }
            setIsApplePayProcessing(true);
            processApplePayPayment(total, (applePayToken: string, details?: PayeeDetails) => {
                setIsApplePayProcessing(false);
                return submitAsync(undefined, applePayToken, details);
            }).catch((error: unknown) => {
                Sentry.captureException(error);
                setIsApplePayProcessing(false);
            });
            return;
        }

        // Saved card: DEFER. The widget verifies, then the caller resumes through
        // `placeOrderWithCaptcha`. Same shape as checkout's `handleCheckout(onShowCaptcha)`.
        paramsRef.current.onShowCaptcha();
    }, [selectedCard, cart.summary, processApplePayPayment, submitAsync]);

    /** Resumes a deferred saved-card submit once the captcha has produced a token. */
    const placeOrderWithCaptcha = useCallback(
        (captchaToken: string) => {
            // Released here rather than on settle: from this point `isSubmitting` covers
            // the button, and a genuinely failed order must be retryable.
            submittingRef.current = false;
            setIsAwaitingCaptcha(false);
            submit(captchaToken);
        },
        [submit]
    );

    /** The captcha was cancelled or failed — release the button so the user can retry. */
    const cancelCaptcha = useCallback(() => {
        submittingRef.current = false;
        setIsAwaitingCaptcha(false);
    }, []);

    return {
        // `isAwaitingCaptcha` keeps Confirm in its loading state while the widget is up:
        // `isSubmitting` does not cover that window, since no saga has started yet.
        isSubmitting: isSubmitting || isApplePayProcessing || isAwaitingCaptcha,
        placeOrder,
        placeOrderWithCaptcha,
        cancelCaptcha
    };
};

export default useFastReorderPlaceOrder;
